import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { STEPS_BY_PAGE, closeTourSideEffects } from "./tourSteps";
import { isTourResolved, setTourStatus } from "../../utils/onboardingStorage";
import "../../styles/onboarding.css";

const PAD = 6;
const TIP_WIDTH = 300;
const INVITE_DELAY_MS = 900;

function computeTooltipPosition(rect, placement = "bottom") {
  const margin = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spaceBelow = vh - (rect.top + rect.height);
  const spaceAbove = rect.top;
  const spaceRight = vw - (rect.left + rect.width);
  const spaceLeft = rect.left;

  let place = placement;
  if (place === "bottom" && spaceBelow < 240 && spaceAbove > spaceBelow) place = "top";
  else if (place === "top" && spaceAbove < 240 && spaceBelow > spaceAbove) place = "bottom";
  else if (place === "left" && spaceLeft < TIP_WIDTH + 24 && spaceRight > spaceLeft) place = "right";
  else if (place === "right" && spaceRight < TIP_WIDTH + 24 && spaceLeft > spaceRight) place = "left";

  const style = { width: TIP_WIDTH };

  if (place === "bottom" || place === "top") {
    let left = rect.left + rect.width / 2 - TIP_WIDTH / 2;
    left = Math.max(margin, Math.min(left, vw - TIP_WIDTH - margin));
    style.left = left;
    if (place === "bottom") style.top = rect.top + rect.height + PAD + 12;
    else style.bottom = vh - (rect.top - PAD) + 12;
  } else {
    let top = rect.top + rect.height / 2 - 70;
    top = Math.max(margin, Math.min(top, vh - 220 - margin));
    style.top = top;
    if (place === "right") style.left = Math.min(rect.left + rect.width + PAD + 12, vw - TIP_WIDTH - margin);
    else style.left = Math.max(margin, rect.left - PAD - 12 - TIP_WIDTH);
  }

  return { style, placement: place };
}

function NeverAgainCheckbox({ checked, onChange, id }) {
  return (
    <label className="tour-never" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>Bir daha gösterme</span>
    </label>
  );
}

/** Birden fazla eşleşmede görünür olan ilk hedefi seçer. */
function findVisibleTarget(selector) {
  const nodes = document.querySelectorAll(selector);
  for (const el of nodes) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

/**
 * İlk ziyaret eden kullanıcıya sayfayı tanıtan adım adım tur.
 * page: 'home' | 'detail'
 */
export default function OnboardingTour({ page }) {
  const steps = useMemo(() => STEPS_BY_PAGE[page] || [], [page]);
  const [phase, setPhase] = useState("idle");
  const [activeSteps, setActiveSteps] = useState([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [neverAgain, setNeverAgain] = useState(false);

  const startTour = useCallback(() => {
    const list = steps.filter((s) => {
      if (s.forceInclude) return true;
      return Boolean(findVisibleTarget(s.target));
    });
    if (!list.length) {
      setPhase("closed");
      return;
    }
    setActiveSteps(list);
    setIndex(0);
    setRect(null);
    setPhase("running");
  }, [steps]);

  const finish = useCallback(
    (status) => {
      try {
        closeTourSideEffects();
      } catch {
        /* yoksay */
      }
      setTourStatus(page, neverAgain ? "dismissed" : status);
      setPhase("closed");
    },
    [page, neverAgain]
  );

  const dismissPrompt = useCallback(() => {
    if (neverAgain) {
      finish("dismissed");
      return;
    }
    setPhase("closed");
  }, [neverAgain, finish]);

  useEffect(() => {
    if (!steps.length || isTourResolved(page)) return;
    const t = setTimeout(() => {
      setPhase((p) => (p === "idle" ? "prompt" : p));
    }, INVITE_DELAY_MS);
    return () => clearTimeout(t);
  }, [page, steps.length]);

  useEffect(() => {
    const onStart = (e) => {
      if (e.detail?.page && e.detail.page !== page) return;
      startTour();
    };
    window.addEventListener("ansiklopedi:start-tour", onStart);
    return () => window.removeEventListener("ansiklopedi:start-tour", onStart);
  }, [page, startTour]);

  const updateRect = useCallback(() => {
    const step = activeSteps[index];
    if (!step) return;
    const el = findVisibleTarget(step.target);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [activeSteps, index]);

  useLayoutEffect(() => {
    if (phase !== "running") return;
    const step = activeSteps[index];
    if (!step) return;
    try {
      step.action?.();
    } catch {
      /* yoksay */
    }
    const bringIntoView = () => {
      const el = findVisibleTarget(step.target);
      if (el) el.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
    };
    bringIntoView();
    updateRect();
    const timers = [
      setTimeout(updateRect, 80),
      setTimeout(updateRect, 200),
      setTimeout(() => {
        bringIntoView();
        updateRect();
      }, 400),
      setTimeout(updateRect, 640),
    ];
    return () => timers.forEach(clearTimeout);
  }, [phase, index, activeSteps, updateRect]);

  useEffect(() => {
    if (phase !== "running") return;
    const onChange = () => updateRect();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [phase, updateRect]);

  useEffect(() => {
    if (phase !== "running" && phase !== "prompt") return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (phase === "running") finish("done");
      else dismissPrompt();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [phase, finish, dismissPrompt]);

  if (phase === "prompt") {
    return createPortal(
      <div className="tour-invite" role="dialog" aria-label="Sayfa tanıtımı">
        <button
          type="button"
          className="tour-invite__close"
          aria-label="Kapat"
          onClick={dismissPrompt}
        >
          ×
        </button>
        <div className="tour-invite__icon" aria-hidden>
          <i className="pi pi-info-circle" />
        </div>
        <p className="tour-invite__title">Bu sayfayı tanımak ister misiniz?</p>
        <p className="tour-invite__text">
          Düğmelerin ne işe yaradığını kısa bir turla adım adım gösterelim.
        </p>
        <div className="tour-invite__actions">
          <button type="button" className="tour-btn tour-btn--primary" onClick={startTour}>
            Evet, gösterin
          </button>
          <button type="button" className="tour-btn" onClick={dismissPrompt}>
            Şimdi değil
          </button>
        </div>
        <NeverAgainCheckbox
          id={`tour-never-invite-${page}`}
          checked={neverAgain}
          onChange={setNeverAgain}
        />
      </div>,
      document.body
    );
  }

  if (phase !== "running") return null;
  const step = activeSteps[index];
  if (!step || !rect) return null;

  const isFirst = index === 0;
  const isLast = index === activeSteps.length - 1;
  const tip = computeTooltipPosition(rect, step.placement);

  return createPortal(
    <div className="tour-root" onMouseDown={(e) => e.stopPropagation()}>
      <div className="tour-catch" />
      <div
        className="tour-spotlight"
        style={{
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
        }}
      />
      <div className="tour-tip" style={tip.style} data-placement={tip.placement} role="dialog">
        <div className="tour-tip__head">
          <span className="tour-tip__step">
            {index + 1} / {activeSteps.length}
          </span>
          <button
            type="button"
            className="tour-tip__close"
            aria-label="Turu kapat"
            onClick={() => finish("done")}
          >
            ×
          </button>
        </div>
        <h4 className="tour-tip__title">{step.title}</h4>
        <p className="tour-tip__body">{step.body}</p>
        <div className="tour-tip__actions">
          <NeverAgainCheckbox
            id={`tour-never-tip-${page}`}
            checked={neverAgain}
            onChange={setNeverAgain}
          />
          <div className="tour-tip__nav">
            {!isFirst && (
              <button type="button" className="tour-btn" onClick={() => setIndex((i) => i - 1)}>
                Geri
              </button>
            )}
            {!isLast && (
              <button
                type="button"
                className="tour-btn tour-btn--primary"
                onClick={() => setIndex((i) => i + 1)}
              >
                İleri
              </button>
            )}
            {isLast && (
              <button type="button" className="tour-btn tour-btn--primary" onClick={() => finish("done")}>
                Bitir
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
